import React, { useEffect, useRef, useState } from 'react';
import type { CTAProps } from './CTAButton';
import { useShopifyProduct } from '../../../../api/shopify/ShopifyProduct';
import { useMinicartUiContext } from '../../../../utils/cart/context/MinicartUiContext';
import { setMiniCartVisible } from '../utils/utils';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { getStoreCurrency } from '../../../../utils/shopify/getStoreCurrency';
import { transformShopifyProductForTracking } from '../../../../utils/cart/transformShopifyProductForTracking';
import { useProducts } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';
import { AttachAddToCartSlideout } from '../../attachAddToCartSlideout/AttachAddToCartSlideout';
import { ActionButton } from '@jbhi-fi/components/src/ActionButton';
import { CartActionState } from '@jbhi-fi/components/src/CartProvider/types';
import { ADD_TO_CART_CONFIRMATION_DURATION_MS } from './AddToCart';
import { ShopifyProductTransformer } from '@jbhi-fi/components/src/Shopify/transformers/ShopifyProductTransformer';
import { getMarketplaceTrackingValuesFromShopify } from '../../../../utils/event-tracking/globalTrackingHelpers';
import { getInsight, postAlgoliaAddToCart } from '../../../../utils/algolia/search-insights';
import { drawCart } from '../../../../utils/legacyCart';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { ShopifyStorefrontClient } from '../../../../api/shopify/ShopifyStorefrontClient';
import { transformStorefrontCartToJbCart } from '../../../../utils/cart/transformStorefrontCartToJbCart';
import { hasReachedMaxQty } from '../../../../utils/checkMaxQty/hasReachedMaxQty';
import { showMaxQtyAlert } from '../../../../utils/checkMaxQty/showMaxQtyAlert';

const storefrontClient = new ShopifyStorefrontClient();

export const PreOrder = ({ productCartData, product, trackingListName }: CTAProps) => {
  const [buttonState, setButtonState] = useState<CartActionState>(CartActionState.Default);
  const [isLoading, setIsLoading] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [modalVisible, setIsModalVisible] = useState<boolean>(false);
  const { shopify, productMetafields, marketplaceSellers } = useShopifyProduct();
  const productSku = shopify?.variants[0]?.sku;
  const productVariantId = product.variants[0].id;
  const { isOpen: isMinicartOpen } = useMinicartUiContext();
  const reviewCount = productMetafields?.Display?.NumberOfProductReviews;
  const reviewScore = productMetafields?.Display?.AverageProductReviewRating;
  const { products: [algoliaProduct] = [] } = useProducts({ productSkus: [productSku] });
  const isMarketplaceProduct = ShopifyProductTransformer.isMarketplaceProduct({ product: shopify });
  const isAttachSlideoutEnabled = window.Jbhifi?.attachConfig?.enabledPages?.pdp && !isMarketplaceProduct;
  const addToCartTrackingData = {
    location: 'pdp',
    currency: getStoreCurrency(),
    addedItems: {
      ...transformShopifyProductForTracking({
        item: productCartData,
        listName: trackingListName ?? 'pdp',
        reviewCount,
        reviewScore,
      }),
      ...getMarketplaceTrackingValuesFromShopify({
        product: shopify,
        marketplaceSellers,
        onlineProduct: productMetafields,
      }),
    },
  };

  const preOrder = async () => {
    if (hasReachedMaxQty(productVariantId)) {
      showMaxQtyAlert();
      return false;
    }

    if (!isLoading && buttonState !== CartActionState.Complete) {
      setIsLoading(true);
    }
    if (isAttachSlideoutEnabled && algoliaProduct) {
      setMiniCartVisible(false);
    } else {
      setMiniCartVisible(isMinicartOpen);
    }

    const properties = {
      _availabilityCanPreOrder: true,
      _availabilityFulfilment: productMetafields?.Availability?.Fulfilment,
      _maxQty: productMetafields?.Availability?.Fulfilment === 'Digital' ? 1 : productMetafields.Product?.LimitPerOrder,
      _priceAtc: shopify?.variants[0]?.price,
    };
    const updatedCart = await storefrontClient.addToCart(productVariantId, 1, filterAndMapAttributes(properties));
    setIsModalVisible(true);
    const transformedCart = transformStorefrontCartToJbCart(updatedCart);
    await drawCart(transformedCart);

    setButtonState(CartActionState.Complete);
    setIsLoading(false);
    trackAddToCart(addToCartTrackingData);
    postAlgoliaAddToCart(productSku, getInsight(productSku));
  };

  useEffect(() => {
    if (buttonState !== CartActionState.Complete) return;
    const timeout = setTimeout(() => setButtonState(CartActionState.Default), ADD_TO_CART_CONFIRMATION_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [buttonState]);

  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.setAttribute('data-state', 'default');
    }
  }, []);

  const onModalClose = () => {
    setIsModalVisible(false);
    window.zE && window.zE('webWidget', 'show');
  };

  return (
    <>
      <ActionButton
        label="Pre-order"
        onClick={preOrder}
        isLoading={isLoading}
        state={buttonState}
        actionFinishedLabel="Added!"
      />
      {isAttachSlideoutEnabled && algoliaProduct ? (
        <AttachAddToCartSlideout
          purchasedProduct={algoliaProduct}
          onClose={onModalClose}
          isOpen={modalVisible}
          pageType="pdp"
        />
      ) : null}
    </>
  );
};

export default PreOrder;
